'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Discount3 } from '@sprd/sprd-component-kit/v2';
import { useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { USPButton } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/components/ProductUsps/ProductUsps';
import { useVolumeDiscount } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/components/VolumeDiscountProvider';
import { volumeDiscountOpenDialogAtom } from '@/atoms/volumeDiscountDialogAtom';
import { tracker } from '@/utility/tracking/tracker';
export type VolumeDiscountActivatorProps = {
  activatorVariant?: 'link' | 'usp';
  /** Marketplace PDP reports the click. Other pages keep the existing data-tracking attribute. */
  trackClick?: boolean;
};

/** Whether there's anything eligible to activate. Returns null when the product has no eligible
 * discount, so callers can bail out before touching any variant-specific markup. */
const useVolumeDiscountDialog = () => {
  const $ = _c(8);
  const {
    discounts
  } = useVolumeDiscount();
  const setOpenDialog = useSetAtom(volumeDiscountOpenDialogAtom);
  let t0;
  if ($[0] !== discounts) {
    t0 = discounts.find(_temp);
    $[0] = discounts;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const firstDiscount = t0;
  if (!firstDiscount) {
    return null;
  }
  let t1;
  if ($[2] !== setOpenDialog) {
    t1 = () => setOpenDialog("volume-discount");
    $[2] = setOpenDialog;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== discounts || $[5] !== firstDiscount || $[6] !== t1) {
    t2 = {
      discounts,
      firstDiscount,
      open: t1
    };
    $[4] = discounts;
    $[5] = firstDiscount;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};

/** Opens the shared volume-discount tier-list dialog owned by the nearest VolumeDiscountProvider.
 * Any number of activators can be rendered anywhere inside a VolumeDiscountProvider — they all
 * open the same dialog instance. Renders nothing when the product has no eligible discount. */
export const VolumeDiscountActivator = t0 => {
  const $ = _c(22);
  const {
    activatorVariant: t1,
    trackClick: t2
  } = t0;
  const activatorVariant = t1 === undefined ? "link" : t1;
  const trackClick = t2 === undefined ? false : t2;
  const t = useTranslations();
  const dialog = useVolumeDiscountDialog();
  if (!dialog) {
    return null;
  }
  const {
    discounts,
    firstDiscount,
    open
  } = dialog;
  let t3;
  if ($[0] !== activatorVariant || $[1] !== open || $[2] !== trackClick) {
    t3 = () => {
      if (trackClick) {
        tracker.trackFeature({
          featureUsed: "volume-discount",
          detail: activatorVariant === "usp" ? "calculate" : "price"
        });
      }
      open();
    };
    $[0] = activatorVariant;
    $[1] = open;
    $[2] = trackClick;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleOpen = t3;
  if (activatorVariant === "usp") {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Discount3 className="size-6" />;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== t) {
      t5 = t("cyoAssortmentDetailPage.volumeDiscount.calculateVolumeDiscount");
      $[5] = t;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== handleOpen || $[8] !== t5) {
      t6 = <USPButton onClick={handleOpen} data-tracking="EVENT - volume discount info">{t4}{t5}</USPButton>;
      $[7] = handleOpen;
      $[8] = t5;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] !== firstDiscount.from || $[11] !== t) {
      t7 = t("cyoAssortmentDetailPage.volumeDiscount.discountFromQuantity", {
        quantity: firstDiscount.from
      });
      $[10] = firstDiscount.from;
      $[11] = t;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== t7) {
      t8 = <span className="ml-8 inline-flex items-center rounded bg-red-200 px-2 py-1 text-sm font-semibold">{t7}</span>;
      $[13] = t7;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== t6 || $[16] !== t8) {
      t9 = <div className="flex w-full flex-col items-start gap-2">{t6}{t8}</div>;
      $[15] = t6;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  let t4;
  if ($[18] !== discounts || $[19] !== handleOpen || $[20] !== t) {
    const highestAvailableDiscount = discounts.reduce(_temp2, 0);
    t4 = <Button variant="plain" className="p-0! text-base! font-semibold! text-red-600 underline" onClick={handleOpen} data-tracking="EVENT - volume discount info">{t("cyoAssortmentDetailPage.volumeDiscount.saveUpTo", {
        discount: highestAvailableDiscount
      })}</Button>;
    $[18] = discounts;
    $[19] = handleOpen;
    $[20] = t;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
};
function _temp(t0) {
  const {
    from,
    value
  } = t0;
  return from && value;
}
function _temp2(highest, discount) {
  return Math.max(highest, discount.value);
}